import { useRecordContext } from "react-admin";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import { parseSpecialProperty } from "../../lib/property-format";

// Only http(s) URLs are safe to render as a clickable href.
// Mirrors isSafeHttpUrl in app/src/lib/security.ts.
function isSafeHttpUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

interface ElementEntry {
  properties?: Record<string, string>;
}

interface PropertiesData {
  elements?: Record<string, ElementEntry>;
}

interface DocLinkRow {
  tipo: "DOC" | "LNK";
  element: string;
  name: string;
  label: string;
  href: string | undefined;
}

// The element map can arrive in two shapes: wrapped as `{ elements: {...} }`
// (initial model upload) or as the bare element map (the viewer's property PATCH
// stores `properties: dataStore.elements` without the wrapper). Mirror the
// viewer's load heuristic (App.tsx) and accept both.
function normalizeElements(
  properties: PropertiesData | null | undefined
): Record<string, ElementEntry> {
  if (!properties || typeof properties !== "object") return {};
  if (properties.elements && typeof properties.elements === "object") {
    return properties.elements;
  }
  return properties as unknown as Record<string, ElementEntry>;
}

function collectRows(properties: PropertiesData | null | undefined): DocLinkRow[] {
  const elements = normalizeElements(properties);

  const rows: DocLinkRow[] = [];

  for (const [elementId, element] of Object.entries(elements)) {
    if (!element.properties) continue;
    for (const [propName, rawValue] of Object.entries(element.properties)) {
      const parsed = parseSpecialProperty(rawValue);
      if (parsed.type !== "DOC" && parsed.type !== "LINK") continue;

      // Deduplicate: skip if we already have a row for the same
      // element + property name + label.
      const alreadyAdded = rows.some(
        (r) =>
          r.element === elementId &&
          r.name === propName &&
          r.label === parsed.label
      );
      if (alreadyAdded) continue;

      rows.push({
        tipo: parsed.type === "DOC" ? "DOC" : "LNK",
        element: elementId,
        name: propName,
        label: parsed.label,
        href: parsed.url && isSafeHttpUrl(parsed.url) ? parsed.url : undefined,
      });
    }
  }

  // Sort DOC before LNK, then by element, then by property name.
  rows.sort((a, b) => {
    if (a.tipo !== b.tipo) return a.tipo === "DOC" ? -1 : 1;
    if (a.element !== b.element) return a.element.localeCompare(b.element);
    return a.name.localeCompare(b.name);
  });

  return rows;
}

export function OtherPropertiesPanel() {
  const record = useRecordContext<{ properties?: PropertiesData }>();

  if (!record) return null;

  const rows = collectRows(record.properties);

  return (
    <Box sx={{ mt: 3, width: "100%" }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        Altre proprietà
      </Typography>

      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nessun documento o hyperlink.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tipo</TableCell>
              <TableCell>Segmento</TableCell>
              <TableCell>Nome proprietà</TableCell>
              <TableCell>Valore</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={`${row.tipo}-${row.element}-${row.name}-${idx}`}>
                <TableCell>{row.tipo}</TableCell>
                <TableCell>{row.element}</TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>
                  {row.href ? (
                    <Link
                      href={row.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      {row.label}
                    </Link>
                  ) : (
                    row.label
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
